/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import _get from 'lodash/get'
import { compose, withHandlers, withProps, withState } from 'recompose'
import { connect } from 'react-redux'
import { reduxForm, getFormValues, change, reset } from 'redux-form'
import { analytics, SITE_SECTIONS } from 'services/Analytics'
import { EMPTY_OBJECT } from 'constants/frozenObjects'
import { USER_FORMS } from 'constants/forms'
import { CARD_TYPES } from 'constants/payment'
import { ALERTS } from 'constants/alerts'
import { withLocalization } from 'hocs/localization'
import { formWithAddress, FORM_COUNTRY_TYPES } from 'hocs/formWithAddress'
import { onRequestToPaymetric, onUpdatePaymentDetails } from 'redux-app/actionCreators/users'
import { onShowError, createAlert } from 'redux-app/modules/alerts'
import { onFinishSendDataToServer, onSendDataToServer } from 'redux-app/modules/form'
import { isCaptchaEnabled, isCaptchaWhiteListed } from 'redux-app/selectors/apiData'
import { openModal } from 'redux-app/modules/modals'
import { getUserDetails, isUserUnableToUpdateInfo, isCaptchaRequiredForCreditCardChange } from 'redux-app/selectors/user'
import { getUserDetails as onGetUserDetails } from 'api/client/actions/user'
import { formWithCaptcha } from 'hocs/formWithCaptcha'
import { getMonthOptions, getYearOptions, getCardTypeByNumber } from 'utils/payment'
import { getCurrentYearNumber, getCurrentMonthIndex } from 'utils/date'
import { getErrorCodes } from 'utils/error'
import { CARD_FIELD_NAMES } from './constants'
import AddCardForm from './AddCardForm'

const FORM_NAME = USER_FORMS.profileAddCard
const expirationMonthOptions = getMonthOptions()
const expirationYearOptions = getYearOptions()

interface IProps {
  redirectUrl: string,
}

export default compose<IProps, {}>(
  connect(
    (state, props) => {
      const userDetails = getUserDetails(state) || EMPTY_OBJECT

      return {
        cardType: getCardTypeByNumber(_get(getFormValues(FORM_NAME)(state), 'paymentCreditCard'), ''),
        cvv: _get(getFormValues(FORM_NAME)(state), 'CVV', ''),
        afterSubmit: props.afterSubmit,
        isUserUnableToUpdateInfo: isUserUnableToUpdateInfo(state),
        cardInfo: _get(userDetails, 'creditCard', ''),
        isCaptchaEnabled: isCaptchaEnabled(state) && isCaptchaRequiredForCreditCardChange(state),
        isCaptchaWhiteListed: isCaptchaWhiteListed(state),
      }
    },
    {
      onShowError,
      createAlert,
      openModal,
      changeField: change,
      onUpdatePaymentDetails,
      onRequestToPaymetric,
      onSendDataToServer,
      onFinishSendDataToServer,
      onGetUserDetails,
    },
  ),
  withLocalization('company.create.addCard.form'),
  withState('isSubmitting', 'setSubmittingState', false),
  withHandlers({
    onSubmit: ({
      onUpdatePaymentDetails,
      onShowError,
      onRequestToPaymetric,
      createAlert,
      afterSubmit,
      setSubmittingState,
      onGetUserDetails,
      isCaptchaEnabled, 
      isCaptchaWhiteListed,
    }) => formValues => {
      setSubmittingState(true)
      const normalizedRequestData = {
        [CARD_FIELD_NAMES.paymentCreditCard]: _get(formValues, CARD_FIELD_NAMES.paymentCreditCard, '')
          .replace(/-/g, ''),
        [CARD_FIELD_NAMES.expMonth]: _get(formValues, CARD_FIELD_NAMES.expMonth),
        [CARD_FIELD_NAMES.expYear]: _get(formValues, CARD_FIELD_NAMES.expYear),
        [CARD_FIELD_NAMES.CVV]: _get(formValues, CARD_FIELD_NAMES.CVV),
        [CARD_FIELD_NAMES.nameOnCard]: _get(formValues, CARD_FIELD_NAMES.nameOnCard).trim(),
      }

      if (isCaptchaEnabled && !isCaptchaWhiteListed && !formValues.captcha) {
        return Promise.resolve()
      }

      return onRequestToPaymetric(normalizedRequestData)
        .then(({ accessToken }) => {
          const updatedCard = {
            paymentAccessToken: accessToken,
            cardHolderName: normalizedRequestData.nameOnCard,
          }
          return onUpdatePaymentDetails({ creditCard: updatedCard })
            .then(() => {
              afterSubmit()
              reset(FORM_NAME)
              onGetUserDetails()
              createAlert({ name: ALERTS.successUpdateCard })
              analytics.saveChanges({ sectionName: SITE_SECTIONS.paymentDetails })
            })
            .catch(error => {
              onShowError(error, formValues)

              if (isCaptchaEnabled && !isCaptchaWhiteListed)
              {
                 window.grecaptcha.reset();
                 formValues.captcha = ''
              }
              
              analytics.showMyAccountError({
                errorCodes: getErrorCodes(error),
                sectionType: SITE_SECTIONS.paymentDetails,
              })
            })
            .finally(() => setSubmittingState(false))
        })
        .catch(() => setSubmittingState(false))
    },
    onChangeCardNumber: ({ cardType, changeField, cvv }) => (event, newValue = '') => {
      if (cardType === CARD_TYPES.amex && getCardTypeByNumber(newValue) !== cardType) {
        changeField(FORM_NAME, CARD_FIELD_NAMES.CVV, cvv.slice(0, 3))
      }
      changeField(FORM_NAME, CARD_FIELD_NAMES.paymentCreditCard, newValue)
    },
  }),
  withProps(() => ({
    expirationMonthOptions,
    expirationYearOptions,
  })),
  reduxForm({
    form: FORM_NAME,
    validate: (values, { getTextPath }) => {
      const errors = {}
      if (_get(values, CARD_FIELD_NAMES.expYear, 0)) {
        const expiredYear = parseInt(`20${_get(values, CARD_FIELD_NAMES.expYear, 0)}`, 10)
        const currentYear = getCurrentYearNumber()
        const expiredMonth = parseInt(_get(values, CARD_FIELD_NAMES.expMonth), 10)
        if (expiredYear < currentYear) {
          errors[CARD_FIELD_NAMES.expYear] = [getTextPath('errors.expiredYearPast')]
        } else if (expiredYear === currentYear && expiredMonth < getCurrentMonthIndex() + 1) {
          errors[CARD_FIELD_NAMES.expMonth] = [getTextPath('errors.expiredMonthPast')]
        }
        if (isCaptchaEnabled && _get(values, CARD_FIELD_NAMES.captcha, '') == '')
        {
          errors[CARD_FIELD_NAMES.captcha] = [getTextPath('errors.noCaptcha')]
        }
      }
      return errors
    },
  }),
  formWithAddress(FORM_COUNTRY_TYPES.billing),
  formWithCaptcha,
)(AddCardForm)
